/**
 * AVA-475 — Slice controller.
 *
 * Creates one fixed, world-axis-aligned measurement volume through the model
 * and point cloud, then orients the camera orthographically around that same
 * volume for XY / XZ / YZ measurement.
 *
 * HOW IT WORKS
 * `Cropping3DExtension.setCropBox(box)` propagates a THREE.Box3 to BOTH the
 * Forge model (`setForgeCropBox`) and every loaded point cloud
 * (`o.setClippingBoxNew(box)`). The box is created/reset independently from
 * camera navigation. XY/XZ/YZ changes only camera pose so the visible point
 * population cannot silently change between views.
 *
 * Each plane is captured by an OPPOSITE PAIR of named views (XY from Top or
 * Bottom, XZ from North or South, YZ from East or West). `setSlice` therefore
 * takes the CHOSEN view id and only falls back to the pair's canonical member
 * when the caller genuinely had no view in hand — see `viewId` below.
 *
 * NOTE: the bundled THREE build is old — `Box3.getCenter()` takes no target and
 * `Vector3` lacks some newer helpers, so vector maths here is hand-rolled.
 * Same constraint computeViewPose.js documents.
 */

import {
  CANONICAL_VIEW_BY_PLANE,
  capturePlaneForView,
  isSlicePlane,
} from '@/features/Verification/utils/asBuiltDraft';
import { computeViewPose } from '@/features/Verification/utils/computeViewPose';
import { assertViewerPoint } from '@/features/Verification/utils/modelUnits';
import { log as debugLog, error as debugError } from '@/utils/debugLogger';

const THREE = window.THREE;

/* NO PLANE TABLE HERE ANY MORE. `PLANE_CONFIG` used to map each plane to ONE
 * view id, and that single field is what collapsed an opposite pair onto its
 * canonical member on every round trip. The camera now comes from the caller's
 * `viewId`; crop geometry stays plane-invariant, as it always was.
 *
 * What remains — "is this a plane a slice can be cut on?" — is already answered
 * by `asBuiltDraft.isSlicePlane` against `PLANE_AXES`, the module that owns the
 * plane vocabulary. A local copy would be a third source of truth in a change
 * whose whole point is removing two. */

/**
 * Fallback measurement-box edge, in VIEWER units — and therefore 200 mm ONLY on
 * a metre-authored model.
 *
 * NOT REACHED TODAY, which is the only reason it is still a bare number. Its one
 * consumer is `setSlice`'s `depth` default, and the single caller
 * (SliceController.vue) always passes an explicit depth — `depthMm` divided by
 * the resolved `mmPerViewerUnit` — and refuses to slice at all while the
 * transform is null. So the default cannot currently fire.
 *
 * IF A CALLER EVER OMITS `depth`, THIS NEEDS THE TRANSFORM. `0.2` is 200 mm on a
 * metre model and 0.2 mm on Merchant: not a wrong-looking slab but an empty one.
 * A unit-independent default has to be computed from `mmPerViewerUnit` at the
 * call site, the same way every other magnitude in this feature now is; there is
 * no correct constant here. The history is instructive — this was `200` under a
 * comment reading "model units", which produced a 200 METRE crop and no
 * isolation whatever, and the fix at the time was to assume metres rather than
 * to ask.
 */
export const DEFAULT_SLICE_DEPTH = 0.2;

/**
 * PURE. Build a fixed cubic measurement volume centred on `center`.
 *
 * `plane` is validated because it is part of the public slicing contract, but
 * deliberately does not influence the bounds. Plane changes rotate the camera
 * around one world-space box; they must never change which points are inside.
 *
 * Kept free of THREE so it can be asserted in isolation (see the Task 0
 * verification script) — the geometry is the part worth proving.
 *
 * EVERY ARGUMENT IS IN VIEWER UNITS, including `depth` — the caller divides
 * millimetres by `mmPerViewerUnit` before calling. Nothing here converts, and
 * nothing here can detect that it should: bounds, centre and depth are all bare
 * numbers, so a metre depth against a millimetre box builds a slab a thousand
 * times too thin and returns it without complaint. Hence the dev guard.
 *
 * @param {{min:{x,y,z}, max:{x,y,z}}} baseBounds  the support's crop box
 * @param {'XY'|'XZ'|'YZ'} plane
 * @param {{x,y,z}} center      box centre — the design point, VIEWER units
 * @param {number} depth        measurement-box edge length, VIEWER units
 * @returns {{min:{x,y,z}, max:{x,y,z}}|null}
 */
export function buildSliceBounds(baseBounds, plane, center, depth) {
  if (!baseBounds || !isSlicePlane(plane) || !center) return null;
  if (!(depth > 0)) return null;

  // Dev-only: the centre is the design point straight from the store, and the
  // box it is measured against is right here — the strongest reference this
  // warning ever gets.
  assertViewerPoint(center, baseBounds);

  const half = depth / 2;
  const min = {
    x: center.x - half,
    y: center.y - half,
    z: center.z - half,
  };
  const max = {
    x: center.x + half,
    y: center.y + half,
    z: center.z + half,
  };

  return { min, max };
}

/** Convert a THREE.Box3 to the plain shape buildSliceBounds works with. */
export function boxToBounds(box) {
  if (!box || !box.min || !box.max) return null;
  return {
    min: { x: box.min.x, y: box.min.y, z: box.min.z },
    max: { x: box.max.x, y: box.max.y, z: box.max.z },
  };
}

/** Convert plain bounds back to a THREE.Box3. */
export function boundsToBox(bounds) {
  if (!bounds) return null;
  return new THREE.Box3(
    new THREE.Vector3(bounds.min.x, bounds.min.y, bounds.min.z),
    new THREE.Vector3(bounds.max.x, bounds.max.y, bounds.max.z),
  );
}

/**
 * Apply a measurement view: fixed crop box + orthographic camera square-on to
 * the plane.
 *
 * @param {object} viewer                Forge viewer
 * @param {object}   opts
 * @param {'XY'|'XZ'|'YZ'} opts.plane
 * @param {{x,y,z}}  opts.center         the design point
 * @param {number}   opts.depth          measurement-box edge length
 * @param {object}   opts.baseBox        THREE.Box3 — the support's crop box
 * @param {boolean}  opts.preserveCrop   keep current box; camera-only move
 * @param {string}   [opts.viewId]       the view the surveyor actually chose.
 *   Omit ONLY when no view was chosen (a re-slice after the design point moved,
 *   a recapture) — the plane's canonical view is then used. Passing a view id
 *   that does not capture `plane` is a caller bug and is refused, because a
 *   camera square to a different plane than the one being sliced is exactly the
 *   mismatch this whole change exists to remove.
 * @returns {boolean} true when the slice was applied
 */
export function setSlice(
  viewer,
  {
    plane,
    center,
    depth = DEFAULT_SLICE_DEPTH,
    baseBox,
    preserveCrop = false,
    savedBounds = null,
    viewId = null,
  } = {},
) {
  if (!viewer) return false;

  if (!isSlicePlane(plane)) {
    debugError('[sliceController] unknown plane', plane);
    return false;
  }

  const bounds = buildSliceBounds(boxToBounds(baseBox), plane, center, depth);
  if (!bounds) {
    debugError('[sliceController] could not build slice bounds', { plane, center, depth });
    return false;
  }

  // A view that does not capture this plane would point the camera at a
  // different projection than the one the pick is about to be attributed to.
  // Refuse it loudly and fall back rather than posing something plausible.
  const canonicalViewId = CANONICAL_VIEW_BY_PLANE[plane];
  let resolvedViewId = viewId || canonicalViewId;
  if (capturePlaneForView(resolvedViewId) !== plane) {
    debugError('[sliceController] view does not capture this plane — using the canonical view', {
      plane, viewId, canonicalViewId,
    });
    resolvedViewId = canonicalViewId;
  }

  try {
    const cropExt = viewer.getExtension?.('Cropping3DExtension');
    if (!cropExt || typeof cropExt.setCropBox !== 'function') {
      debugError('[sliceController] Cropping3DExtension unavailable');
      return false;
    }

    const restoredBox = savedBounds ? boundsToBox(savedBounds) : null;
    const existingBox = preserveCrop ? cropExt.cropBox : null;
    const sliceBox = restoredBox || existingBox || boundsToBox(bounds);

    // Crop Forge AND every point cloud only when the measurement volume is
    // created or explicitly reset. Named-view changes preserve even manual
    // crop-handle adjustments.
    if (!existingBox || restoredBox) cropExt.setCropBox(sliceBox);

    // The ortho views are world-fixed, so `baseBox` no longer decides which way
    // the camera faces — it supplies the framing DISTANCE. Passing the stable
    // support box rather than the live crop is still deliberate: distance drawn
    // from the mutable box would make the camera jitter as the crop handles are
    // dragged. Target/framing comes from the measurement box.
    viewer.navigation?.toOrthographic?.();
    const pose = computeViewPose(resolvedViewId, baseBox, sliceBox);
    if (pose && viewer.navigation?.setView) {
      viewer.navigation.setView(pose.position, pose.target, pose.upVector);
    }
    // IMMEDIATE (true), not animated. An animated fit is still in flight when
    // the caller arms the pick and measures mm-per-px, so both the framing and
    // the screen-scale reading would describe a camera that has already moved.
    // This is the only framing operation for a sliced measurement view. A
    // second fixed-size fit would make a preserved crop appear to reset.
    viewer.navigation?.fitBounds?.(true, sliceBox);

    debugLog('[sliceController] measurement view applied', {
      plane,
      viewId: resolvedViewId,
      depth,
      center,
      cropPreserved: !!existingBox,
      cropRestored: !!restoredBox,
    });
    return true;
  } catch (err) {
    debugError('[sliceController] setSlice failed', err);
    return false;
  }
}

/**
 * Drop the slice and restore the support's full crop box (or clear it entirely).
 * @param {object} viewer
 * @param {object} [restoreBox] THREE.Box3 to restore; omit to clear cropping
 */
export function clearSlice(viewer, restoreBox = null) {
  if (!viewer) return false;
  try {
    const cropExt = viewer.getExtension?.('Cropping3DExtension');
    if (!cropExt || typeof cropExt.setCropBox !== 'function') return false;

    cropExt.setCropBox(restoreBox);
    if (restoreBox) viewer.navigation?.fitBounds?.(false, restoreBox);

    debugLog('[sliceController] slice cleared', { restored: !!restoreBox });
    return true;
  } catch (err) {
    debugError('[sliceController] clearSlice failed', err);
    return false;
  }
}
